import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TrashIcon, Bars3Icon } from '@heroicons/react/24/outline';
import { Input } from '../../../ui/atoms/Input';

const SortableQuestionItem = ({ question, index, onQuestionChange, onPromptChange, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id || index });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-start gap-3 p-3 bg-white border border-gray-200 rounded-md ${isDragging ? 'shadow-lg' : ''}`}
    >
      {/* Drag Handle */}
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 mt-8"
      >
        <Bars3Icon className="w-5 h-5" />
      </div>

      {/* Question Content - Always inputs */}
      <div className="flex-1 space-y-4">
        {/* Question Input */}
        <div>
          <label htmlFor={`question-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
            Question
          </label>
          <Input
            id={`question-${index}`}
            type="text"
            value={question.question || ''}
            onChange={(e) => onQuestionChange(index, e.target.value)}
            placeholder="Question text (shown to user)"
          />
        </div>

        {/* Prompt Input */}
        <div>
          <label htmlFor={`prompt-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
            Prompt
          </label>
          <Input
            id={`prompt-${index}`}
            type="text"
            value={question.prompt || ''}
            onChange={(e) => onPromptChange(index, e.target.value)}
            placeholder="Prompt (optional, defaults to question)"
          />
          <p className="mt-1 text-sm text-gray-500">
            This is the prompt that will be sent to the chat. You can use this to add additional context or instructions that the user won't see.
          </p>
        </div>
      </div>

      {/* Delete Button */}
      <div className="flex items-center">
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
          title="Delete"
        >
          <TrashIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

const QuestionList = ({ questions, onEdit, onRemove, onReorder }) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  if (!questions || questions.length === 0) {
    return null;
  }

  // Add unique IDs to questions if they don't have them
  const questionsWithIds = questions.map((q, index) => ({
    ...q,
    id: q.id || `question-${index}`,
  }));

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = questionsWithIds.findIndex((q) => q.id === active.id);
      const newIndex = questionsWithIds.findIndex((q) => q.id === over.id);

      if (onReorder) {
        onReorder(oldIndex, newIndex);
      }
    }
  };

  const handleQuestionChange = (index, value) => {
    if (onEdit) {
      onEdit(index, value, questionsWithIds[index].prompt);
    }
  };

  const handlePromptChange = (index, value) => {
    if (onEdit) {
      onEdit(index, questionsWithIds[index].question, value);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={questionsWithIds.map((q) => q.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="mb-4 space-y-2">
          {questionsWithIds.map((q, index) => (
            <SortableQuestionItem
              key={q.id}
              question={q}
              index={index}
              onQuestionChange={handleQuestionChange}
              onPromptChange={handlePromptChange}
              onRemove={onRemove}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export default QuestionList;
